import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  useKorintModuleConfig,
  useKorintProductConfig,
  type KorintModulesConfig,
} from 'korint-sdk/react';
import { ComputerIcon, FileTextIcon, MailIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import { Group, Stack } from '@folio/components/ui/flex';

type ContactType = NonNullable<
  NonNullable<KorintModulesConfig['policyholderContactsModuleConfig']>['contacts']
>[number]['type'];

type Contact = NonNullable<
  NonNullable<KorintModulesConfig['policyholderContactsModuleConfig']>['contacts']
>[number];

type PolicyWithBrokerageFirmContact = {
  brokerageFirmContact?: {
    name: string;
    phoneNumber: string;
  };
};

export function resolvePolicyholderContacts(
  config: NonNullable<KorintModulesConfig['policyholderContactsModuleConfig']>,
  policy: PolicyWithBrokerageFirmContact,
): Contact[] {
  if (!config.displayBrokerageFirmContact) return config.contacts ?? [];

  const contact = policy.brokerageFirmContact;
  if (!contact?.name || !contact.phoneNumber) return [];

  return [
    { type: 'info', showIcon: false, value: contact.name },
    { type: 'phone', showIcon: true, value: contact.phoneNumber },
  ];
}

function ContactIcon({ type }: { type: ContactType }) {
  if (type === 'email') {
    return <MailIcon className="h-4 w-4 mr-2" />;
  }
  if (type === 'website') {
    return <ComputerIcon className="h-4 w-4 mr-2" />;
  }
  if (type === 'mail') {
    return <PencilIcon className="h-4 w-4 mr-2" />;
  }
  if (type === 'info') {
    return <FileTextIcon className="h-4 w-4 mr-2" />;
  }
  return <PhoneIcon className="h-4 w-4 mr-2" />;
}

function ContactLink({ type, value }: { type: ContactType; value: string }) {
  if (type === 'email') {
    return (
      <a href={`mailto:${value}`} className="underline">
        {value}
      </a>
    );
  }
  if (type === 'website') {
    return (
      <a href={value} className="underline" target="_blank">
        {value}
      </a>
    );
  }
  if (type === 'mail' || type === 'info') {
    return <span>{value}</span>;
  }
  return (
    <a href={`tel:${value}`} className="underline">
      {value}
    </a>
  );
}

function ContactLabel({ label }: { label?: string }) {
  if (!label) return null;

  return <b className="font-bold">{label}&nbsp;:&nbsp;</b>;
}

export function PolicyholderContactsModule() {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policy.productId]?.policyholderContactsModuleConfig;
  const tenantConfig = useKorintModuleConfig('policyholderContactsModuleConfig');
  const config = productConfig ?? tenantConfig;

  if (!config) return null;

  const { description } = config;
  const contacts = resolvePolicyholderContacts(config, policy as PolicyWithBrokerageFirmContact);

  if (!description && contacts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('policyholderContacts.title')}</CardTitle>
      </CardHeader>
      <Stack asChild>
        <CardContent>
          {description && <p className="whitespace-pre-line">{description}</p>}
          {contacts.map((contact, index) => {
            return (
              <div key={`${contact.value}-${index}`}>
                <Group gap={0}>
                  {contact.showIcon && <ContactIcon type={contact.type} />}
                  <span className="">
                    <ContactLabel label={contact.label} />
                    <span>{contact.prefix}</span>
                    <ContactLink type={contact.type} value={contact.value} />
                  </span>
                </Group>
                <div className="text-gray-600 font-light text-sm ">{contact.extraInfo}</div>
              </div>
            );
          })}
        </CardContent>
      </Stack>
    </Card>
  );
}
